import { useState } from "react";
import { LuBookOpen, LuCamera, LuCheck, LuHash, LuLayoutGrid, LuListOrdered, LuPlus, LuUser, LuX } from "react-icons/lu";
import { Button } from "@/components/ui/button";
import { InputGroup, InputGroupAddon, InputGroupInput } from "@/components/ui/input-group";
import { Label } from "@/components/ui/label";
import {
  NumberField,
  NumberFieldDecrement,
  NumberFieldGroup,
  NumberFieldIncrement,
  NumberFieldInput,
  NumberFieldScrubArea,
} from "@/components/ui/number-field";
import {
  Drawer,
  DrawerFooter,
  DrawerHeader,
  DrawerPanel,
  DrawerPopup,
  DrawerTitle,
} from "@/components/ui/drawer";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import type { Script, StudentGender } from "@/lib/script";
import { createScript, updateScript, type NewScriptInput } from "../hooks/use-scripts";

interface QuestionRow {
  key: number;
  question: string;
  marks: number | null;
}

function parsePerQuestion(json: string | null): QuestionRow[] {
  if (!json) return [];
  try {
    const parsed = JSON.parse(json) as Array<{ question_no: string; marks: number }>;
    return parsed.map((row, i) => ({ key: i, question: row.question_no, marks: row.marks }));
  } catch {
    return [];
  }
}

export function ScriptFormDrawer({
  script,
  onClose,
  onSaved,
}: {
  /** null (or omitted) creates a new script; passing an existing script
   * pre-fills the form and saves as an in-place edit instead. */
  script?: Script | null;
  onClose: () => void;
  onSaved: (script: Script) => void;
}) {
  const editing = !!script;
  const [roll, setRoll] = useState(script?.rollNumber ?? "");
  const [name, setName] = useState(script?.studentName ?? "");
  const [gender, setGender] = useState<StudentGender | null>(script?.gender ?? null);
  const [subject, setSubject] = useState(script?.subject ?? "");
  const [section, setSection] = useState(script?.section ?? "");
  const [totalMarks, setTotalMarks] = useState<number | null>(script?.groundTruthMarksTotal ?? null);
  const [rows, setRows] = useState<QuestionRow[]>(() => parsePerQuestion(script?.groundTruthPerQuestionJson ?? null));
  let nextKey = rows.length;

  function addRow() {
    setRows(r => [...r, { key: nextKey++, question: "", marks: null }]);
  }

  function updateRow(key: number, patch: Partial<QuestionRow>) {
    setRows(r => r.map(row => (row.key === key ? { ...row, ...patch } : row)));
  }

  function removeRow(key: number) {
    setRows(r => r.filter(row => row.key !== key));
  }

  function submit() {
    const trimmedRoll = roll.trim();
    const trimmedName = name.trim();
    const label = [trimmedRoll, trimmedName].filter(Boolean).join(" - ");

    const perQuestion = rows
      .map(r => ({ question_no: r.question.trim(), marks: r.marks }))
      .filter((r): r is { question_no: string; marks: number } => !!r.question_no && r.marks != null && !Number.isNaN(r.marks));

    const input: NewScriptInput = {
      label: label || null,
      rollNumber: trimmedRoll || null,
      studentName: trimmedName || null,
      gender,
      subject: subject.trim() || null,
      section: section.trim() || null,
      groundTruthMarksTotal: totalMarks,
      groundTruthPerQuestionJson: perQuestion.length ? JSON.stringify(perQuestion) : null,
    };

    if (editing) {
      updateScript(script.id, input);
      onSaved({ ...script, ...input });
    } else {
      onSaved(createScript(input));
    }
  }

  return (
    <Drawer open onOpenChange={open => !open && onClose()} position="bottom">
      <DrawerPopup showBar>
        <DrawerHeader>
          <DrawerTitle>{editing ? "Edit script" : "New script"}</DrawerTitle>
        </DrawerHeader>

        <DrawerPanel className="space-y-4">
          <Field label="Roll number">
            <IconInput icon={LuHash} autoFocus value={roll} onChange={e => setRoll(e.target.value)} placeholder="2024001" />
          </Field>
          <Field label="Student name">
            <IconInput icon={LuUser} value={name} onChange={e => setName(e.target.value)} placeholder="Jane Doe" />
          </Field>
          <Field label="Gender">
            <ToggleGroup
              variant="outline"
              className="w-full"
              value={gender ? [gender] : []}
              onValueChange={v => setGender((v[0] as StudentGender) ?? null)}
            >
              <ToggleGroupItem value="male" >Male</ToggleGroupItem>
              <ToggleGroupItem value="female" >Female</ToggleGroupItem>
            </ToggleGroup>
          </Field>
          <Field label="Subject">
            <IconInput icon={LuBookOpen} value={subject} onChange={e => setSubject(e.target.value)} placeholder="Mathematics" />
          </Field>
          <Field label="Section">
            <IconInput icon={LuLayoutGrid} value={section} onChange={e => setSection(e.target.value)} placeholder="A" />
          </Field>
          <NumberField min={0} value={totalMarks} onValueChange={setTotalMarks}>
            <NumberFieldScrubArea label="Ground-truth total marks" />
            <MarksGroup placeholder="100" />
          </NumberField>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <Label>Ground-truth marks per question</Label>
              <Button type="button" variant="ghost" size="sm" onClick={addRow}>
                <LuPlus />
                Add question
              </Button>
            </div>
            {rows.map(row => (
              <div key={row.key} className="mb-2 flex gap-2">
                <IconInput
                  className="flex-2"
                  icon={LuListOrdered}
                  value={row.question}
                  onChange={e => updateRow(row.key, { question: e.target.value })}
                  placeholder="Q1"
                />
                <NumberField
                  className="flex-1"
                  min={0}
                  value={row.marks}
                  onValueChange={value => updateRow(row.key, { marks: value })}
                >
                  <MarksGroup placeholder="5" />
                </NumberField>
                <Button type="button" variant="ghost" size="icon-md" onClick={() => removeRow(row.key)}>
                  <LuX />
                </Button>
              </div>
            ))}
          </div>
        </DrawerPanel>

        <DrawerFooter className="flex-row sm:justify-stretch">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            <LuX />
            Cancel
          </Button>
          <Button className="flex-1" onClick={submit}>
            {editing ? <LuCheck /> : <LuCamera />}
            {editing ? "Save" : "Start capture"}
          </Button>
        </DrawerFooter>
      </DrawerPopup>
    </Drawer>
  );
}

function IconInput({
  icon: Icon,
  className,
  ...props
}: {
  icon: React.ComponentType;
  className?: string;
} & Omit<React.ComponentProps<typeof InputGroupInput>, "className">) {
  return (
    <InputGroup className={className}>
      <InputGroupAddon>
        <Icon />
      </InputGroupAddon>
      <InputGroupInput {...props} />
    </InputGroup>
  );
}

function MarksGroup({ placeholder }: { placeholder: string }) {
  return (
    <NumberFieldGroup>
      <NumberFieldDecrement />
      <NumberFieldInput placeholder={placeholder} />
      <NumberFieldIncrement />
    </NumberFieldGroup>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1.5">
      <Label>{label}</Label>
      {children}
    </div>
  );
}
